import { Checkbox, ChevronDownIcon, HStack, Popover, Pressable, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Text } from '@pimm/base';
import type { SensorColumnDef } from './sensor-table';

type ColumnsPopoverProps = {
  columns: SensorColumnDef[];
  visibleIds: string[];
  onChange: (ids: string[]) => void;
};

export const ColumnsPopover = ({ columns, visibleIds, onChange }: ColumnsPopoverProps) => {
  const { t } = useTranslation();

  const toggle = (id: string) => {
    if (visibleIds.includes(id)) {
      onChange(visibleIds.filter(v => v !== id));
    } else {
      onChange([...visibleIds, id]);
    }
  };

  const handleToggle = (id: string) => () => toggle(id);

  return (
    <Popover
      placement="bottom right"
      trigger={triggerProps => (
        <Pressable
          {...triggerProps}
          rounded="lg"
          px={3}
          h={9}
          shadow={1}
          justifyContent="center"
          borderWidth={1}
          borderColor="gray.300"
          bg="white"
          _pressed={{ opacity: 0.8 }}
        >
          <HStack space={1} alignItems="center">
            <Text size="sm" fontWeight={500} color="gray.700">
              {t('store_equipment:columns')}
            </Text>
            <ChevronDownIcon size={3} color="gray.500" />
          </HStack>
        </Pressable>
      )}
    >
      <Popover.Content rounded="lg" borderWidth={0} w={240}>
        <Popover.Body p={2} bg="transparent">
          <VStack bgColor="white" shadow={1} rounded="lg">
            <HStack px={3} py={1.5}>
              <Text size="sm" fontWeight={600} color="gray.500" textTransform="uppercase">
                {t('store_equipment:columns')}
              </Text>
            </HStack>

            {columns
              .filter(c => !c.actionOnly && !c.exportOnly)
              .map(col => {
                const isChecked = col.alwaysVisible || visibleIds.includes(col.id);
                return (
                  <Pressable
                    key={col.id}
                    px={3}
                    py={col.alwaysVisible ? 3 : 2}
                    isDisabled={col.alwaysVisible}
                    opacity={col.alwaysVisible ? 0.4 : 1}
                    _pressed={{ bg: 'gray.50' }}
                    onPress={handleToggle(col.id)}
                  >
                    <HStack alignItems="center" space={2}>
                      <Checkbox value={col.id} isChecked={isChecked} isReadOnly accessibilityLabel={col.label} />
                      <Text size="md" fontWeight={isChecked ? 600 : 400} color="gray.700">
                        {col.label}
                      </Text>
                    </HStack>
                  </Pressable>
                );
              })}
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
